/*
 * @Author: lizhigang
 * @Date: 2025-10-21 11:05:25
 * @Company: www.smm.cn
 * @Description: 登录
 */
"use client"

import "./index.scss"

import { setClientCookie } from "@repo/shared"
import IconFont from "@repo/ui/iconfont"
import http from "@repo/ui/utils"
import classNames from "classnames"
import md5 from "md5"
import { useLocale, useTranslations } from "next-intl"
import { useSearchParams } from "next/navigation"
import { useCallback, useEffect, useState } from "react"
import { Button, Checkbox, Divider, Form, Input, message } from "antd"

import { useGoogleLogin, usePasswordInput } from "../../_hooks"
import { useMessage } from "../../_hooks/useMessage"
import {
  COOKIE_DEFAULT_KEY,
  COOKIE_TEMPORARY_KEY,
  SIGN_IN_EN_H5_SOURCE_MAP,
  SIGN_IN_EN_WEB_SOURCE_MAP,
} from "../../const"
import { useSigninModal } from "../../index"
import { fetchNecessary, isMobile } from "../../utils"
import { AuthConfigProvider } from "../AuthConfigProvider"

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  schema?: "page" | "modal"
  language?: string
  onLogined?: (msg?: string) => void
  onClose?: () => void
  handleSignUp: () => void
  handleForgot: () => void
}

const Login = ({ schema = "modal", language, onLogined, onClose, handleSignUp, handleForgot, ...attr }: Props) => {
  const locale = useLocale()
  const t = useTranslations("login")
  const searchParams = useSearchParams()
  const urlParam = searchParams.get("url")
  const { modal } = useSigninModal()
  const [form] = Form.useForm()
  const values = Form.useWatch([], form)
  const [account, setAccount] = useState("")
  const [password, setPassword] = useState("")
  const [loading, setLoading] = useState(false)
  const [submittable, setSubmittable] = useState(false)
  const [messageApi, contextHolder] = message.useMessage({
    getContainer: () => document.querySelector("#action") || document.body,
  })

  // 使用 message hook，从 App.useApp() 获取 message 实例
  const { showError } = useMessage({ schema })

  // 使用密码输入 Hook
  const { handlePasswordChange, handlePasswordPaste, handleCompositionStart } = usePasswordInput({
    form,
    fieldName: "password",
    onPasswordChange: setPassword,
  })

  const currentLocale = language || locale || "en"
  const PROFILE_COMPLETE_URL = `${process.env.NEXT_PUBLIC_API_HOST_USER}/${currentLocale}/profile-complete${typeof window !== "undefined" && urlParam ? "?url=" + encodeURIComponent(urlParam) : ""}`

  // 使用 Google 登录 hook
  const { googleLoginRef, GoogleScript } = useGoogleLogin({
    onSuccess: (msg) => {
      if (schema === "modal" && modal) {
        onClose?.()
      }
      onLogined?.(msg)
    },
    locale: currentLocale,
    profileCompleteUrl: PROFILE_COMPLETE_URL,
  })

  /**
   * 登录
   */
  const handleSubmit = async () => {
    if (loading || !account.trim() || !password.trim()) {
      return
    }
    const values = await form.validateFields()
    try {
      if (values) {
        const formData = new FormData()
        formData.append("user_name", values.account.trim())
        formData.append("password", md5(values.password))
        formData.append(
          "source",
          isMobile()
            ? (SIGN_IN_EN_H5_SOURCE_MAP[window.location.host] ?? "")
            : (SIGN_IN_EN_WEB_SOURCE_MAP[window.location.host] ?? "")
        )
        formData.append("language", locale)
        formData.append("source_link", document.referrer || "/")
        formData.append("referrer", window.location.href || "")
        formData.append("user_agent", navigator.userAgent || "")
        // 20260104 第一版不上登录互顶
        // formData.append("oa", "01xwb_en")
        setLoading(true)
        const response = await http.post<FormData, { token: string }>(`/usercenter/english/v1/auth`, {
          data: formData,
          showOriginResponse: true,
          getLang: () => locale,
        })
        setLoading(false)
        if (response?.code === 0) {
          const { token } = response.data!
          const isNecessary = await fetchNecessary(token, locale)
          if (isNecessary) {
            setClientCookie(COOKIE_TEMPORARY_KEY, token, undefined, ".metal.com")
            window.location.href = PROFILE_COMPLETE_URL
          } else {
            if (values.remember) {
              // 设置持久化 cookie，60 天有效期，使用客户端方法来设置cookie是为了避免在登录成功设置cookie后，触发依赖cookie的服务端组件重执行
              setClientCookie(COOKIE_DEFAULT_KEY, token, 60, ".metal.com")
              // await setPersistentCookie(COOKIE_DEFAULT_KEY, token, 60 * 24 * 60 * 60, ".metal.com")
            } else {
              // 设置 session cookie（不设置过期时间），使用客户端方法来设置cookie是为了避免在登录成功设置cookie后，触发依赖cookie的服务端组件重执行
              setClientCookie(COOKIE_DEFAULT_KEY, token, undefined, ".metal.com")
              // await setSessionCookie(COOKIE_DEFAULT_KEY, token, ".metal.com")
            }
            if (schema === "modal" && modal) {
              onClose?.()
            }
            onLogined?.(response.msg)

            // 在后台发送位置信息，不等待完成
            // sendLatLng(locale)
          }
        } else {
          throw new Error(response?.msg)
        }
      }
    } catch (errorInfo) {
      const msg = typeof errorInfo === "string" ? errorInfo : (errorInfo as Error).message
      schema === "modal"
        ? showError(msg || "unknown error")
        : messageApi.open({
            type: "error",
            content: msg || "unknown error",
          })
    }
  }

  // useEffect(() => {
  //   message.config({
  //     getContainer: () => document.getElementById("action")!,
  //     top: schema === "modal" ? 0 : 40,
  //   })
  // }, [schema])

  const sync = useCallback(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setSubmittable(true))
      .catch(() => setSubmittable(false))
  }, [form])

  useEffect(() => {
    sync()
  }, [form, values, sync])

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setSubmittable(true))
      .catch(() => setSubmittable(false))

    // 页面重新可见
    document.addEventListener("visibilitychange", sync)
    window.addEventListener("focus", sync)

    return () => {
      document.removeEventListener("visibilitychange", sync)
      window.removeEventListener("focus", sync)
    }
  }, [sync])

  return (
    <AuthConfigProvider>
      {contextHolder}
      <div className={classNames("loginWrapper", { page: schema === "page", modal: schema === "modal" })} {...attr}>
        <div>
          <div className="logo">
            <IconFont type="icon-SMMlogo-daizimu" style={{ width: 80, height: 32, fontSize: 80 }} />
          </div>
          <h2 className={classNames("modalTitle", { page: schema === "page", modal: schema === "modal" })}>
            {t("title")}
          </h2>
          <Form
            layout="vertical"
            form={form}
            variant="filled"
            autoComplete="off"
            className={"loginForm"}
            requiredMark={false}
          >
            <Form.Item
              name="account"
              label={schema === "page" ? null : t("emailAddress")}
              normalize={(value) => value?.trim()}
              rules={[{ required: true, message: t("requireEmail") }]}
            >
              <Input
                placeholder={t("emailAddress")}
                onChange={(e) => setAccount(e.target.value)}
                onPressEnter={handleSubmit}
                allowClear
                autoFocus
              />
            </Form.Item>
            <Form.Item name="passwordWrapper" className="password-wrapper" style={{ position: "relative" }}>
              {schema === "modal" && (
                <span className={"modalSubTip"} onClick={handleForgot}>
                  {t("forgot")}?
                </span>
              )}
              <Form.Item
                name="password"
                label={schema === "page" ? null : t("password")}
                normalize={(value) => value?.trim()}
                rules={[{ required: true, message: t("requirePassword") }]}
              >
                <Input.Password
                  placeholder={t("password")}
                  onChange={handlePasswordChange}
                  onPressEnter={handleSubmit}
                  onCompositionStart={handleCompositionStart}
                  onPaste={handlePasswordPaste}
                  allowClear
                />
              </Form.Item>
            </Form.Item>
            <Form.Item className="remember" name="remember" valuePropName="checked">
              <div>
                <Checkbox>{t("remember")}</Checkbox>
                {schema === "page" && (
                  <span className={"pageSubTip"} onClick={handleForgot}>
                    {t("forgot")}?
                  </span>
                )}
              </div>
            </Form.Item>
            <Form.Item className="submitButton">
              <Button type="primary" onClick={handleSubmit} disabled={!submittable} loading={loading} block danger>
                {t("signIn")}
              </Button>
            </Form.Item>
          </Form>
          <div className={"bottomTip"}>
            {t("noAccount")}
            <a
              className={"text"}
              onClick={() => {
                handleSignUp()
              }}
            >
              {t("signUp")}
            </a>
          </div>
        </div>
        <div>
          <Divider className={`divider`}>{t("or")}</Divider>
          <div ref={googleLoginRef} className={"googleLogin"}>
            <IconFont type="icon-Google" width={30} height={30} style={{ marginRight: 8, fontSize: 30 }} />
            <span className="text">{t("continue")}</span>
          </div>
        </div>
        <GoogleScript />
      </div>
    </AuthConfigProvider>
  )
}

export default Login
